import React from 'react';
import { FiMenu, FiX, FiVolume2, FiChevronDown, FiCheck, FiSun, FiMoon, FiGlobe, FiActivity } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface VoiceOption {
  id: string;
  name: string;
  gender: string;
  accent: string;
}

interface ChatHeaderProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  selectedSubject: string;
  isZapTalkMode?: boolean;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  selectedVoice: string;
  onSelectVoice: (voiceId: string) => void;
  voiceMenuOpen: boolean;
  onVoiceMenuToggle: (open: boolean) => void;
  availableVoices: VoiceOption[];
  currentVoice?: VoiceOption;
  selectedLanguage: string;
  onSelectLanguage: (language: string) => void;
  languageMenuOpen: boolean;
  onLanguageMenuToggle: (open: boolean) => void;
  availableLanguages: string[];
  zaptalkTopic?: string;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  isSidebarOpen,
  onToggleSidebar,
  selectedSubject,
  isZapTalkMode = false,
  isDarkMode,
  onToggleTheme,
  selectedVoice,
  onSelectVoice,
  voiceMenuOpen,
  onVoiceMenuToggle,
  availableVoices,
  currentVoice,
  selectedLanguage,
  onSelectLanguage,
  languageMenuOpen,
  onLanguageMenuToggle,
  availableLanguages,
  zaptalkTopic = 'Mental Health',
}) => {
  const navigate = useNavigate();
  const shouldHighlightMoodTracker = isZapTalkMode;
  const groupedByGender = ['female', 'male'].map((gender) => ({
    gender,
    voices: availableVoices.filter((voice) => voice.gender === gender),
  }));
  const headerTitle = isZapTalkMode ? selectedSubject : `AI Tutor Chat - ${selectedSubject}`;

  const openMoodTrackerPage = () => {
    navigate('/zaptalk-mood-tracker', {
      state: {
        topic: zaptalkTopic,
        from: '/chat',
        chatMode: 'zaptalk',
      },
    });
  };

  return (
    <header className="chat-header">
      <div className="header-content">
        <button className="mobile-menu-toggle" onClick={onToggleSidebar} aria-label="Toggle menu">
          {isSidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
        <div className="header-left">
          <h1>{headerTitle}</h1>
        </div>
        <div className="header-actions">
          <div className="voice-selector-container" style={{ position: 'relative' }}>
            <button
              className="voice-toggle-btn"
              onClick={() => onVoiceMenuToggle(!voiceMenuOpen)}
              aria-label="Select voice"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: isDarkMode ? '#374151' : '#f3f4f6',
                border: '1px solid',
                borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
                borderRadius: 8,
                color: isDarkMode ? '#e5e7eb' : '#1f2937',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#374151' : '#f3f4f6';
              }}
            >
              <FiVolume2 size={18} />
              <span>{currentVoice?.name || 'Luna'}</span>
              <FiChevronDown size={14} />
            </button>

            {voiceMenuOpen && (
              <>
                <div
                  className="voice-menu-overlay"
                  onClick={() => onVoiceMenuToggle(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }}
                />
                <div
                  className="voice-menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: isDarkMode ? '#1f2937' : '#ffffff',
                    border: '1px solid',
                    borderColor: isDarkMode ? '#374151' : '#e2e8f0',
                    borderRadius: 12,
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    minWidth: 280,
                    maxWidth: '90vw',
                    maxHeight: 400,
                    overflowY: 'auto',
                    zIndex: 999,
                    padding: 8,
                    // Mobile adjustments handled via media queries in CSS if needed,
                    // but inline styles for dynamic values:
                  }}

                >
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid',
                      borderColor: isDarkMode ? '#374151' : '#e2e8f0',
                      marginBottom: 8,
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 14,
                        fontWeight: 600,
                        color: isDarkMode ? '#e5e7eb' : '#1f2937',
                      }}
                    >
                      Select Voice
                    </h3>
                  </div>

                  {groupedByGender.map(({ gender, voices }) => {
                    if (voices.length === 0) return null;
                    return (
                      <div key={gender} style={{ marginBottom: 12 }}>
                        <div
                          style={{
                            padding: '8px 16px',
                            fontSize: 12,
                            fontWeight: 600,
                            color: isDarkMode ? '#9ca3af' : '#6b7280',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          {gender === 'female' ? '👩 Female' : '👨 Male'}
                        </div>
                        {voices.map((voice) => (
                          <button
                            key={voice.id}
                            onClick={() => {
                              onSelectVoice(voice.id);
                              onVoiceMenuToggle(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              textAlign: 'left',
                              background: selectedVoice === voice.id ? '#3b82f6' : 'transparent',
                              color: selectedVoice === voice.id ? '#ffffff' : isDarkMode ? '#e5e7eb' : '#1f2937',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              fontSize: 14,
                              transition: 'all 0.2s ease',
                              marginBottom: 4,
                            }}
                            onMouseEnter={(e) => {
                              if (selectedVoice !== voice.id) {
                                e.currentTarget.style.background = isDarkMode ? '#374151' : '#f3f4f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedVoice !== voice.id) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 500 }}>{voice.name}</div>
                              <div
                                style={{
                                  fontSize: 12,
                                  opacity: 0.7,
                                  marginTop: 2,
                                }}
                              >
                                {voice.accent} • {voice.gender}
                              </div>
                            </div>
                            {selectedVoice === voice.id && <FiCheck size={18} />}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {isZapTalkMode && (
            <button
              type="button"
              className={`voice-toggle-btn mood-tracker-btn${shouldHighlightMoodTracker ? ' is-glowing' : ''}`}
              onClick={openMoodTrackerPage}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: isDarkMode ? '#374151' : '#f3f4f6',
                border: '1px solid',
                borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
                borderRadius: 8,
                color: isDarkMode ? '#e5e7eb' : '#1f2937',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                position: 'relative',
              }}
            >
              <FiActivity size={18} />
              <span style={{ color: '#000000' }}>Mood Tracker</span>
              {shouldHighlightMoodTracker && <span className="mood-tracker-btn__badge">Try</span>}
            </button>
          )}

          <div className="language-selector-container" style={{ position: 'relative' }}>
            <button
              className="language-toggle-btn"
              onClick={() => onLanguageMenuToggle(!languageMenuOpen)}
              aria-label="Select language"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: isDarkMode ? '#374151' : '#f3f4f6',
                border: '1px solid',
                borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
                borderRadius: 8,
                color: isDarkMode ? '#e5e7eb' : '#1f2937',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#374151' : '#f3f4f6';
              }}
            >
              <FiGlobe size={18} />
              <span>{selectedLanguage}</span>
              <FiChevronDown size={14} />
            </button>

            {languageMenuOpen && (
              <>
                <div
                  className="language-menu-overlay"
                  onClick={() => onLanguageMenuToggle(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }}
                />
                <div
                  className="language-menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: isDarkMode ? '#1f2937' : '#ffffff',
                    border: '1px solid',
                    borderColor: isDarkMode ? '#374151' : '#e2e8f0',
                    borderRadius: 12,
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    minWidth: 260,
                    maxWidth: '90vw',
                    maxHeight: 360,
                    overflowY: 'auto',
                    zIndex: 999,
                    padding: 8,
                  }}
                >
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid',
                      borderColor: isDarkMode ? '#374151' : '#e2e8f0',
                      marginBottom: 8,
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 14,
                        fontWeight: 600,
                        color: isDarkMode ? '#e5e7eb' : '#1f2937',
                      }}
                    >
                      Select Language
                    </h3>
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: isDarkMode ? '#9ca3af' : '#6b7280',
                      }}
                    >
                      Voice responses are available only in English.
                    </div>
                  </div>

                  <div
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'copy';
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const language = event.dataTransfer.getData('text/plain');
                      if (language) {
                        onSelectLanguage(language);
                        onLanguageMenuToggle(false);
                      }
                    }}
                    style={{
                      margin: '8px 12px 12px',
                      padding: '12px',
                      borderRadius: 10,
                      border: '1px dashed',
                      borderColor: isDarkMode ? '#4b5563' : '#cbd5f5',
                      background: isDarkMode ? '#111827' : '#f8fafc',
                      color: isDarkMode ? '#cbd5f5' : '#475569',
                      fontSize: 12,
                      lineHeight: 1.4,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Drag a language here</div>
                    <div>Selected: {selectedLanguage}</div>
                  </div>

                  {availableLanguages.map((language) => (
                    <button
                      key={language}
                      onClick={() => {
                        onSelectLanguage(language);
                        onLanguageMenuToggle(false);
                      }}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.setData('text/plain', language);
                        event.dataTransfer.effectAllowed = 'copy';
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        textAlign: 'left',
                        background: selectedLanguage === language ? '#10b981' : 'transparent',
                        color: selectedLanguage === language ? '#ffffff' : isDarkMode ? '#e5e7eb' : '#1f2937',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        transition: 'all 0.2s ease',
                        marginBottom: 4,
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLanguage !== language) {
                          e.currentTarget.style.background = isDarkMode ? '#374151' : '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLanguage !== language) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <span>{language}</span>
                      {selectedLanguage === language && <FiCheck size={18} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            className="theme-toggle"
            onClick={onToggleTheme}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
          </button>
        </div>
      </div>
    </header >
  );
};

export default ChatHeader;
